import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../contexts/ToastContext';
import { CheckCircle, XCircle, Info, X } from '@phosphor-icons/react';

export const ToastContainer: React.FC = () => {
  let toasts: Array<{ id: string; message: string; type?: 'success' | 'error' | 'info'; duration?: number; action?: { label: string; onClick: () => void } }> = [];
  let removeToast: (id: string) => void = () => {};

  try {
    const toastContext = useToast();
    toasts = toastContext.toasts;
    removeToast = toastContext.removeToast;
  } catch {
    // Toast context not available (SSR or not wrapped in provider)
    return null;
  }

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 top-6 left-6 md:top-auto md:left-auto z-[60] flex flex-col gap-3 pointer-events-none items-center md:items-end">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: {
    id: string;
    message: string;
    type?: 'success' | 'error' | 'info';
    duration?: number;
    action?: {
      label: string;
      onClick: () => void;
    };
  };
  onRemove: () => void;
}

// Keep in sync with the jc-toast-out keyframe duration in components/toast.css.
const EXIT_ANIMATION_MS = 180;

const ACCENT_VAR: Record<'success' | 'error' | 'info', string> = {
  success: 'var(--jc-positive)',
  error: 'var(--jc-negative)',
  info: 'var(--jc-accent-soft)',
};

const ToastItem: React.FC<ToastItemProps> = ({ toast, onRemove }) => {
  const [leaving, setLeaving] = useState(false);
  const duration = toast.duration ?? 0;
  const remainingRef = useRef(duration);
  const timerStartedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const requestClose = useCallback(() => {
    setLeaving(true);
  }, []);

  const armTimer = useCallback(
    (ms: number) => {
      if (ms <= 0) return;
      timerStartedAtRef.current = Date.now();
      timerRef.current = setTimeout(requestClose, ms);
    },
    [requestClose]
  );

  useEffect(() => {
    armTimer(remainingRef.current);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Only ever (re)armed on mount / explicit hover pause-resume below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(onRemove, EXIT_ANIMATION_MS);
    return () => clearTimeout(t);
  }, [leaving, onRemove]);

  const handleMouseEnter = () => {
    if (duration <= 0 || leaving || !timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const elapsed = Date.now() - timerStartedAtRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);
  };

  const handleMouseLeave = () => {
    if (duration <= 0 || leaving || timerRef.current) return;
    armTimer(remainingRef.current);
  };

  const type = toast.type || 'success';
  const icon = {
    success: <CheckCircle className="h-5 w-5" weight="fill" />,
    error: <XCircle className="h-5 w-5" weight="fill" />,
    info: <Info className="h-5 w-5" weight="fill" />,
  }[type];

  const cssVars = {
    '--toast-accent': ACCENT_VAR[type],
    '--toast-duration': `${Math.max(duration, 0)}ms`,
  } as React.CSSProperties;

  return (
    <div
      className="jc-toast-item pointer-events-auto min-w-[280px] sm:min-w-[300px] max-w-md w-full md:w-auto"
      data-leaving={leaving || undefined}
      style={cssVars}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="jc-toast rounded-3xl px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3 border border-black/5 dark:border-white/10"
        role="alert"
      >
        <div className="jc-toast-icon flex-shrink-0 mt-0.5 h-7 w-7 rounded-full flex items-center justify-center">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-base font-semibold leading-tight text-jc-ink">
            {toast.message}
          </div>
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick();
                requestClose();
              }}
              className="jc-toast-action mt-2.5 w-full sm:w-auto px-3 py-1.5 rounded-lg text-sm font-medium transition-colors active:scale-95 cursor-pointer"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={requestClose}
          className="flex-shrink-0 text-jc-ink-secondary hover:text-jc-ink transition-colors active:scale-95 mt-0.5 cursor-pointer"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        {duration > 0 && <div className="jc-toast-meniscus rounded-full" />}
      </div>
    </div>
  );
};
